import { PasswordInput } from '@/_components/inputs/password-input'
import type { TSignInInputDTO } from '@doutorsim/shared/auth'
import { SignInInputDTOSchema } from '@doutorsim/shared/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useRouter } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '../../_components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../_components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../_components/ui/form'
import { Input } from '../../_components/ui/input'
import { authClient } from '../_lib/auth-client'
import { AuthLayout } from './_components/auth-layout'
import { AuthLegalLinks } from './_components/auth-legal-links'

export const LoginScene = () => {
  const [isPending, startTransition] = useTransition()

  const router = useRouter()

  const methods = useForm<TSignInInputDTO>({
    resolver: zodResolver(SignInInputDTOSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: TSignInInputDTO) => {
    startTransition(async () => {
      const { error } = await authClient.signIn.email({
        email: data.email,
        password: data.password,
      })
      if (error) {
        toast.error('Erro ao fazer login', {
          description: 'Verifique suas credenciais e tente novamente.',
        })
        return
      }

      router.invalidate()
    })
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-[420px] border-0 p-8 shadow-[0_12px_48px_rgba(26,33,52,0.11)]">
        <CardHeader className="p-0 pb-6">
          <CardTitle>Login</CardTitle>
          <CardDescription>Faça login para acessar nosso sistema.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Form {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => onSubmit(data))}
              className="flex flex-col gap-5"
            >
              <FormField
                control={methods.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Insira seu email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Senha</FormLabel>
                      <Link
                        to="/login/forgot-password"
                        className="text-xs font-medium text-brand-green-primary-5 transition-colors hover:text-brand-green-primary-6"
                      >
                        Esqueceu sua senha?
                      </Link>
                    </div>
                    <FormControl>
                      <PasswordInput placeholder="Insira sua senha" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Entrar
              </Button>
            </form>
          </Form>

          <AuthLegalLinks />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
